import React, { useState, useEffect } from 'react';
import { X, Download, Eye, Clock, CheckCircle, Trash2 } from 'lucide-react';
import { documentService } from '../../services/documentService';
import { parseServerDate } from '../../utils/datetime';
import { useLanguage } from '../../i18n';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

const VersionHistoryDialog = ({ documentId, documentName, onClose, onPreview }) => {
    const { t } = useLanguage();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                setLoading(true);
                const data = await documentService.getDocumentVersions(documentId);
                setVersions(data.versions || []);
            } catch (err) {
                setError(userErrorMessage(err, t));
            } finally {
                setLoading(false);
            }
        };
        fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [documentId]);

    const handleDownload = async (versionId, _versionNum) => {
        try {
            await documentService.downloadDocument(versionId, documentName);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handlePreview = (versionId) => {
        if (onPreview) {
            onPreview(versionId, documentName);
        }
    };

    // Version Bin logic (#237): permanently delete THIS specific (non-latest)
    // version — its row, chunks, and GCS blob — to free storage. This is a
    // permanent deletion (no Recently-Deleted trash), so it is gated behind an
    // explicit confirm, distinct from the recoverable File-Manager delete.
    const handleDelete = async (versionId, versionNum) => {
        if (deletingId) return;
        const ok = window.confirm(t('fmDeleteVersionConfirm', 'Permanently delete version {n}? This cannot be undone.', { n: versionNum }));
        if (!ok) return;
        setDeletingId(versionId);
        setError(null);
        try {
            const data = await documentService.deleteDocumentVersion(versionId);
            // The BE returns the refreshed list — use it directly.
            setVersions(data.versions || []);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setDeletingId(null);
        }
    };

    // Localized to the viewer's timezone; naive server timestamps are treated as
    // UTC first (Localization of Upload Timestamps #63).
    const formatDate = (dateStr) => {
        const date = parseServerDate(dateStr);
        if (!date) return t('fmUnknown', 'Unknown');
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSize = (bytes) => {
        if (!bytes) return t('fmUnknown', 'Unknown');
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Action-icon classes — hidden until the version box is hovered, revealed in
    // the project's neutral "whitish" token (matching #235), colouring only on
    // DIRECT icon hover. `accent` is 'blue' (Preview/Download) or 'red' (Delete).
    //
    // ⚠️ DELIBERATELY NOT MIGRATED to the PAI palette. version-bin-logic.spec.js
    // asserts these exact tokens on the rendered buttons — `text-gray-500`,
    // `dark:text-white`, `hover:text-blue-500`, `hover:text-red-500` — plus the
    // computed `opacity` 0 → 1 on hover. Renaming them to `text-pai-*` or moving
    // the reveal off `opacity` breaks that spec. On this ground `dark:text-white`
    // is already the resting colour, so it reads correctly as-is.
    const iconBtnClass = (accent) => {
        const hover = accent === 'red'
            ? 'hover:text-red-500 dark:hover:text-red-400'
            : 'hover:text-blue-500 dark:hover:text-blue-400';
        return `opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity text-gray-500 dark:text-white ${hover} p-1.5 rounded`;
    };

    return (
        <Modal onClose={onClose} size="lg" layout="column" closeOnBackdrop={false} cardClassName="max-h-[70dvh]" cardTestId="version-history-dialog">
                {/* Header */}
                <div className="px-6 py-4 border-b border-pai-hairline flex justify-between items-center gap-3 flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-[19px] font-bold text-white">{t('fmVersionHistory', 'Version History')}</h3>
                        <p className="text-[13px] text-pai-meta truncate max-w-[350px]">{documentName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-8 text-pai-meta">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/70 mr-3"></div>
                            {t('fmLoadingVersions', 'Loading versions...')}
                        </div>
                    )}

                    {error && (
                        <div className="text-pai-red text-center py-4 text-[13px]">{error}</div>
                    )}

                    {!loading && versions.length === 0 && !error && (
                        <div className="text-white/40 text-center py-8 text-[14.5px]">{t('fmNoVersionHistory', 'No version history available')}</div>
                    )}

                    {!loading && versions.length > 0 && (
                        <div className="space-y-3">
                            {versions.map((v) => (
                                <div
                                    key={v.id}
                                    data-testid="version-row"
                                    className={`group p-4 rounded-row border ${
                                        v.is_latest
                                            ? 'border-pai-green/30 bg-pai-green/[0.05]'
                                            : 'border-pai-subtle bg-white/[0.025]'
                                    } ${deletingId === v.id ? 'opacity-50 pointer-events-none' : ''}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-white text-[14.5px] tnum">
                                                {t('fmVersionN', 'Version {n}', { n: v.version })}
                                            </span>
                                            {v.is_latest && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-[0.06em] border border-pai-green/50 bg-pai-green/[0.12] text-pai-green">
                                                    <CheckCircle size={12} />
                                                    {t('fmCurrent', 'Current')}
                                                </span>
                                            )}
                                        </div>
                                        {/* Action icons — hidden until the box is hovered (#237). */}
                                        <div className="flex items-center gap-1">
                                            <button
                                                data-testid="version-preview"
                                                onClick={() => handlePreview(v.id)}
                                                className={iconBtnClass('blue')}
                                                title={t('commonPreview', 'Preview')}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                data-testid="version-download"
                                                onClick={() => handleDownload(v.id, v.version)}
                                                className={iconBtnClass('blue')}
                                                title={t('commonDownload', 'Download')}
                                            >
                                                <Download size={16} />
                                            </button>
                                            {/* Bin: only on NON-latest versions — the current
                                                version can't be pruned (delete the document). */}
                                            {!v.is_latest && (
                                                <button
                                                    data-testid="version-delete"
                                                    onClick={() => handleDelete(v.id, v.version)}
                                                    className={iconBtnClass('red')}
                                                    title={t('fmDeleteVersionPermanently', 'Delete this version permanently')}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-[12.5px] text-pai-meta tnum">
                                        <span data-testid="version-date" className="flex items-center gap-1">
                                            <Clock size={12} />
                                            {formatDate(v.created_at)}
                                        </span>
                                        <span data-testid="version-size">{formatSize(v.file_size)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
    );
};

export default VersionHistoryDialog;
